import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Loader2 } from "lucide-react";
import { useApi } from "@/lib/api";
import { eur, num } from "@/lib/format";
import { useTheme } from "@/lib/theme";

const monthLabel = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString("de-DE", { month: "short", year: "2-digit" });

const Tip = ({ active, payload }) =>
  active && payload?.length ? (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs shadow-lg">
      <div className="text-slate-500">{monthLabel(payload[0].payload.month)}</div>
      <div className="num text-sm font-semibold text-orange-700">{eur(payload[0].value)}</div>
      <div className="num text-slate-500">{num(payload[0].payload.kg, 1)} kg</div>
    </div>
  ) : null;

const Stat = ({ label, t, testId }) => (
  <div data-testid={testId}>
    <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
    <div className="num mt-1 text-2xl font-bold">{eur(t.revenue)}</div>
    <div className="num text-xs text-slate-500">{num(t.kg, 1)} kg · {t.count} {t.count === 1 ? "Anlieferung" : "Anlieferungen"}</div>
  </div>
);

export const useDeliveryStats = (deps = []) => {
  const api = useApi();
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/deliveries/stats").then((r) => setStats(r.data)).catch(() => setStats(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, ...deps]);
  return stats;
};

export default function RevenuePanel({ stats, showLink = true }) {
  const dark = useTheme() === "dark";
  if (stats === false) return <div className="panel p-5 text-sm text-slate-500 md:col-span-3 lg:col-span-4">Umsatzstatistik derzeit nicht verfügbar.</div>;
  if (!stats) return <div className="panel flex h-48 items-center justify-center md:col-span-3 lg:col-span-4"><Loader2 className="h-5 w-5 animate-spin text-orange-600" /></div>;
  return (
    <div className="panel p-5 md:col-span-3 lg:col-span-4" data-testid="revenue-panel">
      <div className="mb-5 flex items-center justify-between">
        <h3 className="text-base font-semibold">Umsatz aus Anlieferungen</h3>
        {showLink && <Link to="/anlieferungen" className="text-xs text-orange-700 hover:underline" data-testid="link-deliveries">Anlieferungen erfassen</Link>}
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
        <div className="grid grid-cols-2 gap-5 lg:grid-cols-1">
          <Stat label="Dieser Monat" t={stats.this_month} testId="stat-revenue-month" />
          <Stat label="Letzte 30 Tage" t={stats.last_30} testId="stat-revenue-30" />
          <Stat label="Gesamt" t={stats.total} testId="stat-revenue-total" />
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500">Ø Erlös je kg</div>
            <div className="num mt-1 text-2xl font-bold" data-testid="stat-avg-price">{eur(stats.total.avg_eur_kg, 3)}</div>
          </div>
        </div>
        <div className="h-64 lg:col-span-2" data-testid="chart-revenue-monthly">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={stats.monthly} margin={{ left: 0, right: 8, top: 4 }}>
              <CartesianGrid stroke={dark ? "rgba(255,255,255,0.06)" : "#EDE7E0"} vertical={false} />
              <XAxis dataKey="month" tickFormatter={monthLabel} tick={{ fill: "#64748B", fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={(v) => num(v, 0)} tick={{ fill: "#64748B", fontSize: 11, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} width={56} />
              <Tooltip content={<Tip />} cursor={{ fill: "rgba(234,88,12,0.06)" }} />
              <Bar dataKey="revenue" fill="#EA580C" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="space-y-4">
          {[["Nach Sorte", stats.by_grade], ["Nach Wertstoffhof", stats.by_yard]].map(([title, rows]) => (
            <div key={title}>
              <div className="mb-2 text-xs uppercase tracking-wider text-slate-500">{title}</div>
              {rows.length === 0 ? <p className="text-sm text-slate-500">Noch keine Daten.</p> : rows.slice(0, 5).map((r) => (
                <div key={r.name} className="flex justify-between gap-2 py-1 text-sm">
                  <span className="truncate text-slate-600">{r.name}</span>
                  <span className="num shrink-0 font-medium">{eur(r.revenue, 0)}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
